import { createFileRoute } from "@tanstack/react-router";

import { CopyableValue } from "../components/CopyableValue";
import { Footer } from "../components/Footer";
import { Container } from "../components/ui";
import { t } from "../i18n";

export const Route = createFileRoute("/vilkar")({
  component: TermsPage,
});

function TermsPage() {
  const s = t.terms;
  // /vilkar is a public route (see PUBLIC_PREFIXES in __root.tsx) so the
  // page renders bare without AppShell. We mount Footer inline so the
  // privacy + terms links stay reachable from any page in the app.
  return (
    <div className="flex min-h-screen flex-col bg-ink-50">
      <Container className="flex-1">
        <article className="prose prose-slate dark:prose-invert max-w-2xl py-8">
          <header className="mb-8">
            <h1 className="mb-3 font-semibold text-3xl text-ink-900">{s.pageTitle}</h1>
            <p className="text-base text-ink-700 leading-relaxed">{s.pageLead}</p>
            <p className="mt-3 text-ink-500 text-xs">{s.lastUpdated}</p>
          </header>

          <Section title={s.sections.acceptance.title}>
            <p>{s.sections.acceptance.body}</p>
          </Section>

          <Section title={s.sections.service.title}>
            <p>{s.sections.service.body}</p>
          </Section>

          <Section title={s.sections.account.title}>
            <p>{s.sections.account.body}</p>
            <ul>
              {s.sections.account.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.acceptableUse.title}>
            <p>{s.sections.acceptableUse.body}</p>
            <ul>
              {s.sections.acceptableUse.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="text-ink-700">{s.sections.acceptableUse.consequence}</p>
          </Section>

          <Section title={s.sections.yourContent.title}>
            <p>{s.sections.yourContent.body}</p>
            <ul>
              {s.sections.yourContent.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </Section>

          <Section title={s.sections.availability.title}>
            <p>{s.sections.availability.body}</p>
          </Section>

          <Section title={s.sections.liability.title}>
            <p>{s.sections.liability.body}</p>
            <ul>
              {s.sections.liability.bullets.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <p className="text-ink-700">{s.sections.liability.cap}</p>
          </Section>

          <Section title={s.sections.termination.title}>
            <p>{s.sections.termination.body}</p>
          </Section>

          <Section title={s.sections.law.title}>
            <p>{s.sections.law.body}</p>
          </Section>

          <Section title={s.sections.contact.title}>
            <p>
              {s.sections.contact.body}
              <CopyableValue
                value={s.sections.contact.contactEmail}
                hint={s.sections.contact.copyEmailHint}
                doneLabel={s.sections.contact.copyEmailDone}
              />
              {s.sections.contact.contactAfter}
            </p>
          </Section>
        </article>
      </Container>
      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 font-semibold text-ink-900 text-xl">{title}</h2>
      <div className="space-y-3 text-ink-800 leading-relaxed">{children}</div>
    </section>
  );
}
